import { ReturnTotalsCalculator } from "@redotech/redo-model/return-totals-calculator";
import { getLocations, getProduct, getSettings } from "../api";

export async function setFavicon() {
  const response = await getSettings();
  const settings = response?.data;
  if (settings?.theme.logo_url) {
    const link = document.createElement("link");
    link.type = "image/x-icon";
    link.rel = "shortcut icon";
    link.href = settings.theme.logo_url;
    document.head.appendChild(link);
  }
}

export function optional<T, V>(
  value: T | null | undefined,
  fn: (value: T) => V,
): V | null {
  if (value === null || value === undefined) {
    return null;
  }

  return fn(value);
}

export const extractPriceFromProperties = (lineItem) => {
  const originItemPrice = optional(
    lineItem.properties.find(({ name }) => name === "_redo_origin_item"),
    ({ value }) => {
      const payload: {
        unitPrice?: string;
      } = JSON.parse(value);

      return payload?.unitPrice || null;
    },
  );

  return originItemPrice
    ? {
        original_price: originItemPrice,
        discount_price: originItemPrice,
      }
    : null;
};

export const hasRedoDiscountCode = (data) => {
  return data.shopify.discount_applications?.some((discount) =>
    discount.code?.includes("REDO"),
  );
};

export const makeOrder = async (data) => {
  const tempData = data;
  const lineItems = data.shopify.line_items.filter(
    (item) => item.vendor !== "re:do",
  );
  const productIds = [];
  lineItems.forEach((item) => {
    if (!productIds.includes(item.product_id)) {
      productIds.push(item.product_id);
    }
  });

  const products = [];
  await Promise.all(
    productIds.map(async (productId) => {
      products.push((await getProduct(productId)).data);
    }),
  );
  const modifiedLineItems = lineItems.map((item) => {
    const extractedPrice = extractPriceFromProperties(item);
    // Won't make api request if it already has for that product.
    let product = products.find((product) => product.id === item.product_id);

    return {
      ...item,
      ...{
        processed_at: new Date(data.shopify.processed_at),
        // now being done with FRE
        // available_strategies: {
        //   refund: item.options.refunds,
        //   exchange: item.options.exchange,
        //   store_credit: item.options.store_credit,
        // },
        shipping_fees: {
          store_credit: 0,
          exchange: 0,
          refund: 0,
        },
        coverage: {
          exchange: data.protected && data.coverage.exchange,
          refund: data.protected && data.coverage.refund,
          store_credit: data.protected && data.coverage.storeCredit,
        },
        original_price: item.price,
        discount_price: ReturnTotalsCalculator.getDiscountPrice(item, data),
        green_return_selected: false,
        green_return_type: "discount", //Default value
        return: null, // This is to define what we do with the product.
        product: product,
        fixed_strategies: [],
        is_exchange_order: data.isExchangeOrder,
        is_defective: null,
        is_wrong_item: null,
        needs_manual_review: false,
        // We use this flag to determine if we should split this single item in a new
        // return. This isu used for returns that require approval by the merchant.
        split: false,
        inputAnswers: [], // should be type InputAnswer
        readyToReturn: false, // Set this to true once user has completed flow for item.
        selected_return_option: null,
        selected_quantity: 0,
        new_item: null,
        returnReason: undefined,
        upsell: undefined,
        restocking_fee: undefined,
        initialLineItem: undefined,
        ...extractedPrice,
      },
    };
  });
  tempData.line_items = modifiedLineItems;
  return tempData;
};

export const enum LocationCondition {
  PRODUCT_TAGS = "product_tags",
  DAMAGED = "damaged",
}

async function getFulfilledFromAddress(locationId) {
  const response = await getLocations();

  const location = response.data.locations.find(
    (location) => location.id === locationId,
  );
  if (location) {
    return {
      city: location.city,
      country: location.country,
      country_name: location.country_name,
      name: location.name,
      phone: location.phone,
      state: location.province_code,
      street1: location.address1,
      street2: location.address2,
      zip: location.zip,
    };
  } else {
    // No original location
    return null;
  }
}

async function getAddress(address, locationId, originalLocation) {
  if (originalLocation) {
    const originalAddress = await getFulfilledFromAddress(locationId);
    if (originalAddress) {
      return originalAddress;
    }
  }
  return address;
}
export const getMerchantAddress = async (
  locationSettings,
  productTags,
  returnReason,
  fulfilledFromLocationId,
) => {
  for (const location of locationSettings) {
    switch (location.condition) {
      case LocationCondition.PRODUCT_TAGS:
        const locationTags = location.tags.map((tag) => tag.toLowerCase());
        const matchingTags = productTags.filter((tag) =>
          locationTags.includes(tag.toLowerCase()),
        );
        if (matchingTags.length > 0) {
          return getAddress(
            location.address,
            fulfilledFromLocationId,
            location.originalLocation,
          );
        }
        break;
      case LocationCondition.DAMAGED:
        if (
          returnReason.toLowerCase().includes("damage") ||
          returnReason.toLowerCase().includes("broken") ||
          returnReason.toLowerCase().includes("defective")
        ) {
          return getAddress(
            location.address,
            fulfilledFromLocationId,
            location.originalLocation,
          );
        }
        break;
      default:
        // Default address
        return getAddress(
          location.address,
          fulfilledFromLocationId,
          location.originalLocation,
        );
        break;
    }
  }
};

const emailRegex = /\S+@\S+\.\S+/;
export const isValidEmail = (email: string): boolean => emailRegex.test(email);
